/* eslint-disable @typescript-eslint/naming-convention */
import { takeLatest, call, put } from 'redux-saga/effects';
import { initialize } from 'redux-form';

import {
  SETTING_PUBLIC_PROFILE_PAGE_REQUEST,
  SETTING_PUBLIC_PROFILE_REQUEST,
} from '../../actions/setting/publicProfile/constants';
import * as action from '../../actions';
import * as actNotif from '../../actions/notifications';
import { setFile } from '../../actions/uploadImage';

import { getProfile } from '../../api/profile';
import { settingPublicProfile } from '../../api/publicProfile';
import * as apiUploadImage from '../../api/s3';
import { phoneNumberFormatter } from '../../utils/formatter';
import { BUSINESS } from '../../utils/uploadS3Constants';
import { constructFormData } from '../../utils/s3Util';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapSettingsPublicProfile from '../../clevertap/settings/publicProfile';

export function* publicProfilePageRequestFlow() {
  try {
    yield put(action.sendingRequest(true));
    yield put(action.showProgressBar());
    const resProfile = yield call(getProfile);
    const profile = resProfile.data;
    clevertapSettingsPublicProfile.settingPublicProfileStart({
      'business Name': profile.business.name || '',
      'business Description': profile.business.description || '',
      'business Address': profile.business.address || '',
      province: profile.business.province || '',
      city: profile.business.city || '',
      'postal Code': profile.business.postal_code || '',
      'phone Number': profile.business.phone || '',
      email: profile.business.email || '',
      web: profile.business.website || '',
      twitter: profile.business.twitter || '',
      facebook: profile.business.facebook || '',
      instagram: profile.business.instagram || '',
    });
    yield put(action.setProfile(profile));
    // set initial value of public profile form at first load
    yield put(
      initialize('publicProfileForm', {
        gravatar: profile.gravatar,
        name: profile.business.name,
        description: profile.business.description,
        address: profile.business.address,
        city: profile.business.city,
        province: profile.business.province,
        postal_code: profile.business.postal_code,
        phone: profile.business.phone
          ? phoneNumberFormatter(profile.business.phone)
          : '',
        email: profile.business.email,
        website: profile.business.website,
        twitter: profile.business.twitter,
        facebook: profile.business.facebook,
        instagram: profile.business.instagram,
      })
    );
    yield put(setFile(null));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
  }
}

export function* publicProfileRequestFlow(request) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const {
    values: {
      name,
      description,
      address,
      city,
      province,
      postal_code,
      phone,
      email,
      website,
      twitter,
      facebook,
      instagram,
    },
    user: profile,
    file,
  } = request.payload.data;
  let {
    values: { gravatar },
  } = request.payload.data;
  const businessId = profile.business.id;
  const { business } = profile;

  // upload image
  if (file) {
    const formData = constructFormData(file, BUSINESS, businessId);
    const resUpload = yield call(apiUploadImage.postNewImage, formData, {});
    gravatar = resUpload.data.download_image_url;
  }

  const data = {
    business: {
      id: businessId,
      name,
      description,
      address,
      city,
      province,
      postal_code,
      phone: `0${phone}`,
      email,
      website,
      twitter,
      facebook,
      instagram,
      logo: { url: gravatar },
      suite: business.suite,
      notify_per_deposit: business.notify_per_deposit,
      notify_per_trans: business.notify_per_trans,
      user_id: business.user_id,
      is_deleted: business.is_deleted,
      created_at: business.created_at,
      business_type_id: business.business_type_id,
      business_category_id: business.business_category_id,
      notes: business.notes,
      latitude: business.latitude,
      longitude: business.longitude,
      synchronized_at: business.synchronized_at,
      outlet_slot: business.outlet_slot,
      is_trial: business.is_trial,
      current_plan: business.current_plan,
      expired_at: business.expired_at,
      last_payment_date: business.last_payment_date,
      trial_end_at: business.trial_end_at,
      setting: business.setting,
    },
  };
  // https://mokapos.atlassian.net/browse/FD-1720
  // somehow backend bakal rusak kalo dia upload remote_logo_url tapi sama dengan yang sebelumnya
  // jadi hanya kirim remote_logo kalo beda dengan sebelumnya
  // wasting time amat
  let gravatarChanged = gravatar === '';

  gravatarChanged =
    gravatarChanged || (business.logo && business.logo.url) !== gravatar;

  if (gravatarChanged) {
    (data.business as any).remote_logo_url = gravatar;
    (data.business as any).remove_logo = gravatar === '';
  }
  try {
    yield call(settingPublicProfile, businessId, data);

    const resProfile = yield call(getProfile);

    const profileData = resProfile.data;
    yield put(action.setProfile(profileData));

    // set initial value of public profile after submitting form
    yield put(
      initialize('publicProfileForm', {
        gravatar: profileData.business.logo
          ? profileData.business.logo.url
          : '',
        name,
        description,
        address,
        city,
        province,
        postal_code,
        phone: phoneNumberFormatter(`0${phone}`),
        email,
        website,
        twitter,
        facebook,
        instagram,
      })
    );
    yield put(actNotif.success('Business successfully edited.'));
    clevertapSettingsPublicProfile.settingPublicProfileSaveSuccess(
      data.business
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export default function* settingPublicProfileSaga() {
  yield takeLatest(
    SETTING_PUBLIC_PROFILE_PAGE_REQUEST,
    publicProfilePageRequestFlow
  );
  yield takeLatest(SETTING_PUBLIC_PROFILE_REQUEST, publicProfileRequestFlow);
}
